import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Users,
  IndianRupee,
  TrendingUp,
  Plus,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { getDashboardStats } from "@/services/superAdminService";
import OtpModal from "@/components/OtpModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await getDashboardStats();

      setStats(response);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      toast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Companies",
      value: stats?.total_companies || 0,
      icon: Building2,
      color: "emerald",
      description: "Active companies on platform",
    },
    {
      title: "Total Users",
      value: stats?.total_users || 0,
      icon: Users,
      color: "blue",
      description: "Admins & employees combined",
    },
    {
      title: "Total Employees",
      value: stats?.total_employees || 0,
      icon: Users,
      color: "indigo",
      description: "Across all companies",
    },
    {
      title: "Payroll Runs",
      value: stats?.total_payroll_runs || 0,
      icon: IndianRupee,
      color: "amber",
      description: "Total payroll processed",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Super Admin Dashboard
          </h1>
          <p className="mt-2 text-muted-foreground">
            Manage all companies and monitor platform activity
          </p>
        </div>
        <Link to="/super-admin/companies/add">
          <Button className="mt-4 sm:mt-0">
            <Plus className="h-4 w-4 mr-2" />
            Add New Company
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          const colorClasses = {
            emerald:
              "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
            blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
            indigo:
              "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
            amber:
              "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
          };
          return (
            <Card
              key={stat.title}
              className="hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </p>
                    <p className="mt-2 text-3xl font-bold text-foreground">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {stat.description}
                    </p>
                  </div>
                  <div
                    className={`p-3 rounded-full ${colorClasses[stat.color]}`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Companies */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Companies</CardTitle>
          <Link to="/super-admin/companies">
            <Button variant="ghost" size="sm">
              View All
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.recent_companies && stats.recent_companies.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_companies.map((company) => (
                <div
                  key={company.company_id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-primary dark:text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">
                        {company.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {company.contact_email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${company.status === "active"
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-muted text-foreground dark:bg-muted dark:text-muted-foreground"
                        }`}
                    >
                      {company.status}
                    </span>
                    <Button
                      onClick={() =>
                        navigate("/super-admin/companies/details", {
                          state: { companyId: company.id },
                        })
                      }
                      variant="ghost"
                      size="sm"
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No companies yet
              </p>
              <Link to="/super-admin/companies/add">
                <Button className="mt-4" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Company
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-4">
              <Building2 className="h-6 w-6 text-primary dark:text-emerald-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-foreground">
              Manage Companies
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              View, edit, and manage all companies on the platform
            </p>
            <Link to="/super-admin/companies">
              <Button variant="outline" className="w-full">
                Go to Companies
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4">
              <Plus className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-foreground">
              Add New Company
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Onboard a new company to the Zenith platform
            </p>
            <Link to="/super-admin/companies/add">
              <Button className="w-full bg-primary hover:bg-emerald-700">
                Add Company
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-foreground">
              Platform Analytics
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              View detailed analytics and reports across companies
            </p>
            <Button variant="outline" className="w-full" disabled>
              Coming Soon
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
